// The Code Next "wow" moment (ADO #574).
//
// The Problem Solving course never shows code — with ONE exception Ram
// approved: at a real milestone, show the learner the flow they just built
// beside the real C it becomes. That moment is the bridge to Code Next and the
// paid C course, so it is kept to FIVE moments (Ram, 2026-10-15), each at most
// once per learner:
//
//   first_program    first pass of an item on ps-m2 in-build
//                    (read a value, work on it, show the answer)
//   first_decision   first pass on ps-m4 dec-build
//   first_loop       first pass on ps-m5 whl-build or for-build, whichever first
//   own_functions    first pass on ps-m8 par-build
//   capstone:<slug>  the pass that FINISHES a capstone — every story passing
//
// Only a PASSED, freshly graded attempt (a duplicate resubmit is not news), and
// only in the free course. Objective slugs repeat across modules (nest-build is
// in ps-m4 and ps-m5), so a moment is matched on (module, objective).
//
// Where the item sits comes from the attempt response's `placement` (course,
// module, objective, capstone, and whether the capstone is now finished), which
// the server adds to every coding attempt.
//
// "Already shown" lives on the device, scoped to the signed-in learner the
// same way quiz answers and tours are (learnerScope). Best-effort: storage that
// throws means the moment may repeat, never that grading breaks.
import { scopedKey } from './learnerScope';

export const FREE_COURSE_PREFIX = 'problem-solving';
// Where "See the C course" goes — the same cart route the dashboard's
// "Enroll now" uses for a paid course.
export const C_COURSE_SLUG = 'c-programming';
export const C_COURSE_HREF = `/cart?add=${encodeURIComponent(C_COURSE_SLUG)}`;

// `${module}/${objective}` -> moment.
export const OBJECTIVE_MOMENTS = {
  'ps-m2/in-build': 'first_program',
  'ps-m4/dec-build': 'first_decision',
  'ps-m5/whl-build': 'first_loop',
  'ps-m5/for-build': 'first_loop',
  'ps-m8/par-build': 'own_functions',
};

// Each moment's headline and the one line under it.
const COPY = {
  first_program: {
    headline: 'Your flow is a real C program!',
    line: 'Every step you placed just became a line of C, the language behind Linux, Git and '
      + 'PostgreSQL. You already did the hard part: the thinking.',
  },
  first_decision: {
    headline: 'Your program now makes decisions!',
    line: 'That Decision you drew is an if / else in C, the same choice every app on your phone '
      + 'makes millions of times a day.',
  },
  first_loop: {
    headline: 'Your program repeats on its own!',
    line: 'Your loop is real C now. One flow, and the computer does the work again and again, '
      + 'as many times as it takes.',
  },
  own_functions: {
    headline: 'You designed your own functions!',
    line: 'Each subflow became a C function that takes values and hands one back. That is how '
      + 'professional programs are organised.',
  },
  capstone: {
    // Capstone titles are imperatives ("Build mini-sed"); the headline wants
    // the thing built, so the leading "Build" goes.
    headline: (title) => `You built ${String(title || '').replace(/^build\s+/i, '') || 'your capstone'}!`,
    line: 'Every story passed. Your flows are a working tool in real C, built the way the '
      + 'original was. Imagine writing it yourself.',
  },
};

/** The headline and line for one moment; every `capstone:<slug>` shares one. */
export function wowCopy(moment, capstoneTitle = null) {
  const key = String(moment || '').startsWith('capstone:') ? 'capstone' : moment;
  const c = COPY[key] || COPY.first_program;
  return {
    headline: typeof c.headline === 'function' ? c.headline(capstoneTitle) : c.headline,
    line: c.line,
  };
}

const EMPTY = { shown: {} };

/**
 * Which moment, if any, this attempt earns. Pure: memory is passed.
 *
 * @param {object} args
 * @param {object} args.verdict  the attempt response ({ passed, duplicate, placement })
 * @param {string} args.ref      the item or story slug that passed
 * @param {object} [args.memory] { shown: { [moment]: ts } }
 * @returns {{ moment: string, module: string, ref: string, capstoneTitle: string|null } | null}
 */
export function pickWowMoment({ verdict, ref, memory = EMPTY }) {
  if (!verdict || !verdict.passed || verdict.duplicate) return null;
  const p = verdict.placement;
  if (!p || !String(p.course_slug || '').startsWith(FREE_COURSE_PREFIX)) return null;

  let moment = null;
  if (p.capstone_slug) {
    // A story earns it only as the pass that leaves every story done.
    if (p.capstone_finished === true) moment = `capstone:${p.capstone_slug}`;
  } else {
    moment = OBJECTIVE_MOMENTS[`${p.module_slug}/${p.objective_slug}`] || null;
  }
  if (!moment) return null;
  if ((memory || EMPTY).shown?.[moment]) return null;
  return {
    moment,
    module: p.module_slug || null,
    ref: ref || null,
    capstoneTitle: p.capstone_slug ? (p.capstone_title || null) : null,
  };
}

const storageKey = () => scopedKey('codenext-wow', 'memory');

export function readWowMemory() {
  try {
    const raw = localStorage.getItem(storageKey());
    const parsed = raw ? JSON.parse(raw) : null;
    if (!parsed || typeof parsed !== 'object') return { shown: {} };
    return { shown: parsed.shown || {} };
  } catch (_) {
    return { shown: {} };
  }
}

export function recordWowShown(moment, now = Date.now()) {
  const mem = readWowMemory();
  const next = { shown: { ...mem.shown, [moment]: now } };
  try {
    localStorage.setItem(storageKey(), JSON.stringify(next));
  } catch (_) {
    /* private mode / quota — the moment may repeat, nothing else breaks */
  }
  return next;
}

// Fold in the moments the SERVER says this learner has already seen (any device,
// any site: a preview link has its own localStorage). Kept separate from
// recordWowShown so a moment seen elsewhere is remembered without a new event.
export function mergeWowSeen(moments, now = Date.now()) {
  const mem = readWowMemory();
  const shown = { ...mem.shown };
  for (const m of moments || []) if (m && !shown[m]) shown[m] = now;
  try {
    localStorage.setItem(storageKey(), JSON.stringify({ shown }));
  } catch (_) {
    /* best-effort */
  }
  return { shown };
}

// ---------------------------------------------------------------------------
// The learner's flow as a lesson <FlowDiagram> spec: the same node cards the
// canvas draws, read-only. Card title is the node's label, subtitle its
// description — exactly what the canvas card shows.

function toStep(node) {
  const step = { type: node.type, label: node.label || undefined, note: node.description || undefined };
  if ((node.type === 'decision' || node.type === 'switch') && Array.isArray(node.outcomes)) {
    step.branches = node.outcomes.map((o) => ({
      label: o.label || (o.isDefault ? 'Default' : (o.caseValue != null ? String(o.caseValue) : '')),
      steps: flowNodesToSteps(o.nodes),
    }));
  } else if (node.type === 'try_catch' && Array.isArray(node.branches)) {
    step.branches = node.branches.map((b) => ({
      label: b.label || b.type || '',
      steps: flowNodesToSteps(b.nodes),
    }));
  } else if (node.type === 'loop') {
    step.body = flowNodesToSteps(node.loopBody);
    const after = flowNodesToSteps(node.afterLast);
    if (after.length) step.afterLast = after;
  }
  return step;
}

export function flowNodesToSteps(nodes) {
  return (Array.isArray(nodes) ? nodes : []).filter(Boolean).map(toStep);
}

const isContract = (f) => f?.flowType === 'contract';
const isEntry = (f) => f?.flowType === 'main' || f?.isEntry === true;

/** The flows worth showing — every one but the Contract Flow — entry first. */
export function showableFlows(flows) {
  const list = (Array.isArray(flows) ? flows : []).filter((f) => f && !isContract(f));
  return [...list.filter(isEntry), ...list.filter((f) => !isEntry(f))];
}

/** The submitted files, main file first. */
export function orderedFiles(filesDict, mainFile) {
  const names = Object.keys(filesDict || {});
  if (mainFile && names.includes(mainFile)) {
    return [mainFile, ...names.filter((n) => n !== mainFile)];
  }
  return names;
}
